import type { FC, ReactNode } from 'react'

import { History } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/dates'
import { Select } from '@/components/ui/select'

type HistoryPickerProps = {
    id: string
    title: string
    description: string
    emptyText: string
    dates: string[]
    value: string
    onSelect: (date: string) => void
    className?: string
}

// "Lihat ... sebelumnya": jump to a date that already has saved records
export const HistoryPicker: FC<HistoryPickerProps> = ({
    id,
    title,
    description,
    emptyText,
    dates,
    value,
    onSelect,
    className
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-3 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-3.5 sm:flex-row sm:items-center sm:justify-between', className)}>
            <div className='flex min-w-0 items-start gap-3'>
                <span className='grid size-9 shrink-0 place-items-center rounded-lg bg-primary/12 text-primary' aria-hidden='true'>
                    <History className='size-[18px]' />
                </span>

                <span className='min-w-0'>
                    <strong className='block text-[13px] font-semibold text-foreground'>
                        {title}
                    </strong>

                    <span className='block text-xs leading-relaxed text-muted-foreground'>
                        {description}
                    </span>
                </span>
            </div>

            <div className='w-full shrink-0 sm:w-60'>
                <label htmlFor={id} className='mb-1 block text-xs font-semibold text-muted-foreground'>
                    Tanggal tersimpan
                </label>

                <Select
                    id={id}
                    value={dates.includes(value) ? value : ''}
                    disabled={dates.length === 0}
                    onChange={event => {
                        if (event.target.value) onSelect(event.target.value)
                    }}
                    className='h-9'
                >
                    <option value=''>Pilih tanggal tersimpan</option>

                    {dates.map(date => (
                        <option key={date} value={date}>
                            {formatDate(date, 'short')}
                        </option>
                    ))}
                </Select>

                {dates.length === 0 && (
                    <p className='mt-1 text-xs text-muted-foreground'>
                        {emptyText}
                    </p>
                )}
            </div>
        </div>
    )
}
